import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material';
import { employeeApi, accessApi } from '../../services/accessApi';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    employeeStats: { byStatus: [], byDepartment: [] },
    accessStats: { total: 0, active: 0, revoked: 0, bySystem: [] }
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [empResponse, accessResponse] = await Promise.all([
        employeeApi.getStats(),
        accessApi.getStats()
      ]);

      setStats({
        employeeStats: empResponse.data.data,
        accessStats: accessResponse.data.data
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status) => {
    const stat = stats.employeeStats.byStatus.find(s => s._id === status);
    return stat ? stat.count : 0;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Overview
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Active Employees
                  </Typography>
                  <Typography variant="h4">
                    {getStatusCount('Active')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <HourglassIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Pending Onboarding
                  </Typography>
                  <Typography variant="h4">
                    {getStatusCount('Pending Onboarding')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CancelIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Offboarded
                  </Typography>
                  <Typography variant="h4">
                    {getStatusCount('Offboarded')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Employees
                  </Typography>
                  <Typography variant="h4">
                    {stats.employeeStats.byStatus.reduce((sum, s) => sum + s.count, 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Employees by Department
              </Typography>
              <Box sx={{ mt: 2 }}>
                {stats.employeeStats.byDepartment.map((dept, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1, borderBottom: '1px solid #eee' }}
                  >
                    <Typography variant="body1">{dept._id}</Typography>
                    <Typography variant="h6" color="primary">
                      {dept.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Access Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1, borderBottom: '1px solid #eee' }}
                >
                  <Typography variant="body1">Total Assignments</Typography>
                  <Typography variant="h6" color="primary">
                    {stats.accessStats.total}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1, borderBottom: '1px solid #eee' }}
                >
                  <Typography variant="body1">Active Access</Typography>
                  <Typography variant="h6" color="success.main">
                    {stats.accessStats.active}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Typography variant="body1">Revoked Access</Typography>
                  <Typography variant="h6" color="error.main">
                    {stats.accessStats.revoked}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Systems by User Count
              </Typography>
              <Box sx={{ mt: 2 }}>
                {stats.accessStats.bySystem.slice(0, 10).map((system, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 1, borderBottom: index < 9 ? '1px solid #eee' : 'none' }}
                  >
                    <Typography variant="body1">{system.systemName}</Typography>
                    <Typography variant="h6" color="primary">
                      {system.count} users
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
